import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Building2, Lock, CreditCard, ShieldAlert, CheckCircle2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Passwordless rent payment landing page.
 * Reached from the "Pay rent now →" button in the monthly rent-reminder email.
 * Flow:
 *   1. Page loads /pay/:token, we call GET /api/rent-link/verify/:token
 *      → returns lease info (rent, property, landlord, accept toggle)
 *   2. Tenant taps "Pay with card" → POST /api/rent-link/checkout/:token
 *      → returns Stripe Checkout URL → window.location = url
 *   3. Stripe handles card entry + 3DS + redirects to /payment/success
 *
 * No login required. Token is JWT-signed (rent_pay purpose, 14-day TTL).
 */
export default function PayRent() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | ready | paying | expired | error
  const [info, setInfo] = useState(null);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/rent-link/verify/${token}`);
        setInfo(data);
        setPhase("ready");
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 410) {
          setErrorText(detail || "This payment link has expired.");
          setPhase("expired");
        } else if (status === 404) {
          setErrorText(detail || "Lease not found.");
          setPhase("error");
        } else {
          setErrorText(detail || "We couldn't load this payment link. Try again in a moment.");
          setPhase("error");
        }
      }
    })();
  }, [token]);

  const pay = async () => {
    setPhase("paying");
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/rent-link/checkout/${token}`,
        { lease_id: info.lease_id, origin_url: window.location.origin },
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Could not start checkout.";
      setErrorText(detail);
      setPhase("ready");
    }
  };

  const fmtUSD = (n) =>
    "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="payrent-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (phase === "expired" || phase === "error") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payrent-error">
        <div className="w-full max-w-md bg-white border border-red-200 rounded-sm p-6 sm:p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            {phase === "expired" ? "Link expired" : "Can't open this link"}
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="payrent-error-message">{errorText}</p>
          <p className="text-xs text-slate-400 mt-5">
            You can still pay rent from your tenant portal. Ask your landlord to resend the reminder.
          </p>
        </div>
      </div>
    );
  }

  const blocked = !info.accept_rent_payments;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payrent-page">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center gap-3">
          {info.landlord_logo_data_uri ? (
            <img
              src={info.landlord_logo_data_uri}
              alt={info.landlord_company || "Landlord logo"}
              className="w-9 h-9 object-contain rounded-sm border border-slate-200 bg-white p-0.5"
              data-testid="payrent-logo"
            />
          ) : (
            <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Pay rent</div>
            <div className="text-sm font-bold text-slate-900 truncate" data-testid="payrent-landlord">
              {info.landlord_company || "Your landlord"}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 sm:py-10 space-y-5">
        {/* Amount hero */}
        <section
          className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 text-center"
          data-testid="payrent-amount-card"
        >
          <div className="overline text-slate-500 mb-2">Amount due</div>
          <div
            className="text-4xl sm:text-5xl font-bold font-mono tracking-tight text-slate-900"
            data-testid="payrent-amount"
          >
            {fmtUSD(info.monthly_rent)}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-700">
            <div className="font-semibold" data-testid="payrent-property">{info.property_name}</div>
            {info.unit_label && (
              <div className="text-slate-500 mt-0.5" data-testid="payrent-unit">{info.unit_label}</div>
            )}
            {info.property_address && (
              <div className="text-xs text-slate-400 mt-1">{info.property_address}</div>
            )}
          </div>
          {info.tenant_name && (
            <div className="mt-4 text-xs text-slate-500">
              For <span className="font-semibold text-slate-700">{info.tenant_name}</span>
            </div>
          )}
        </section>

        {blocked && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-900 leading-relaxed"
            data-testid="payrent-blocked-banner"
          >
            <div className="font-semibold mb-1">Online payments paused</div>
            Your landlord has temporarily disabled online rent payments. Please contact them directly to arrange payment.
          </div>
        )}

        {/* Pay CTA */}
        <button
          type="button"
          onClick={pay}
          disabled={phase === "paying" || blocked}
          data-testid="payrent-pay-button"
          className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-base sm:text-lg font-bold py-4 rounded-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {phase === "paying" ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Stripe…
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" /> Pay {fmtUSD(info.monthly_rent)}
            </>
          )}
        </button>

        {errorText && phase === "ready" && (
          <div className="text-sm text-red-600 text-center" data-testid="payrent-inline-error">{errorText}</div>
        )}

        {/* Trust strip */}
        <div className="flex items-center justify-center gap-5 text-xs text-slate-500 pt-2">
          <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Secured by Stripe</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> No login required</span>
        </div>

        <p className="text-xs text-slate-400 text-center leading-relaxed pt-2">
          Receipts are issued automatically once payment settles.
        </p>
      </main>
    </div>
  );
}
